import { useEffect, useRef } from "react";
import { TabKey } from "../types";

/**
 * Custom hook for managing tab scroll behavior
 * Ensures the active tab is visible when the page loads or refreshes
 */
export function useTabScroll(activeTab: TabKey) {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Function to scroll to active tab on mobile
    const scrollToActiveTab = () => {
      if (!tabsContainerRef.current || !activeTabRef.current) return;

      // Check if we're on mobile/tablet (where horizontal scroll is needed)
      const isMobile = window.innerWidth < 1024; // lg breakpoint

      if (isMobile) {
        // Scroll the tab container to show the active tab
        const container = tabsContainerRef.current;
        const activeTab = activeTabRef.current;

        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        // Calculate scroll position to center the active tab
        const scrollLeft =
          activeTab.offsetLeft -
          container.clientWidth / 2 +
          activeTab.clientWidth / 2;

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });
      }
    };

    // Scroll on mount (page load/refresh)
    scrollToActiveTab();

    // Optional: Re-scroll on window resize
    const handleResize = () => {
      scrollToActiveTab();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab]);

  return {
    tabsContainerRef,
    activeTabRef,
  };
}
